import * as React from "react";
import { clsx } from "@plans/appkit";
import type { TypedOmit } from "@plans/appkit";
import { NavLink } from "react-router";
import { controlSizeClassMap } from "~/ui/constants";
import type { ControlSize, ResponsiveProperty } from "~/ui/types";
import { useResponsiveSize } from "~/ui/useResponsiveSize";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "text";

export type ButtonProps = TypedOmit<
	React.ComponentPropsWithoutRef<"button">,
	"disabled"
> & {
	variant: ButtonVariant;
	as?: "span" | "div";
	size?: ResponsiveProperty<ControlSize>;
	isLoading?: boolean;
	isDisabled?: boolean;
	isRound?: boolean;
	isIconOnly?: boolean;
	innerShadowClassName?: string;
	/**
	 * If specified, will render an <a> tag if it's an absolute URL, otherwise a
	 * <Link> component.
	 */
	href?: string;
	target?: "_blank" | "_self" | "_parent" | "_top";
	rel?: "noreferrer" | "noopener";
	testId?: string;
	ref?: React.Ref<HTMLButtonElement | null>;
};

export function Button({
	variant,
	size = "medium",
	isLoading,
	isDisabled,
	isRound,
	className,
	children,
	type,
	as,
	href,
	target,
	testId,
	isIconOnly,
	rel,
	name,
	innerShadowClassName,
	"aria-disabled": ariaDisabled,
	ref,
	...htmlProps
}: ButtonProps) {
	let Tag: typeof NavLink | "div" | "span" | "button" | "a";
	let hyperLinkProps: { to: string } | { href: string } | object = {};

	if (href) {
		// https://stackoverflow.com/a/19709846
		const isAbsoluteURL = new RegExp("^(?:[a-z+]+:)?//", "i").test(href);
		Tag = isAbsoluteURL ? "a" : NavLink;
		hyperLinkProps = isDisabled ? {} : Tag === "a" ? { href } : { to: href };
	} else {
		Tag = as ?? "button";
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const TagAsAny = Tag as any;
	const sizeClassList = useResponsiveSize(size, controlSizeClassMap);
	// Google Translate can replace raw text nodes and break React's DOM reconciliation.
	// Wrap text-like children in spans so React updates/removes stable element nodes.
	// NOTE: each string child (incl. the bare " " between `{a} {b}`) becomes its own
	// flex item, so on a Button with a `gap-*` class a multi-part label renders with
	// extra spacing. Pass labels as a single string child (e.g. a template literal).
	// See /docs/react-components.md#button-text-spacing
	const renderedChildren = React.Children.map(children, (child, index) => {
		if (typeof child === "number" || typeof child === "string") {
			return <span key={`button-text-${index}`}>{child}</span>;
		}

		return child;
	});

	return (
		<TagAsAny
			{...htmlProps}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			ref={ref as any}
			type={Tag === "button" ? (type ?? "button") : undefined}
			className={clsx(
				...sizeClassList,
				"global-focus inline-flex items-center justify-center py-1 focus-visible:outline-offset-1",
				"cursor-pointer select-none whitespace-nowrap text-center font-semibold leading-none",
				"rounded-sm border-[1px]",
				"disabled:cursor-not-allowed aria-disabled:cursor-not-allowed",
				variantShadowClassMap[variant],
				variantClassMap[variant],
				isRound && "aspect-square !rounded-full !px-0",
				isIconOnly && "aspect-square !p-0",
				variant === "text" && href && "[&:not(:disabled)]:hover:underline",
				className?.includes("absolute") || className?.includes("fixed")
					? undefined
					: "relative",
				className,
			)}
			name={name}
			aria-disabled={ariaDisabled || isDisabled}
			disabled={isDisabled || isLoading}
			target={href ? target : undefined}
			rel={href ? rel : undefined}
			data-cy={testId ?? name}
			{...hyperLinkProps}
		>
			{renderedChildren}
			{isLoading && <Spinner className="h-[1.15lh] w-[1.15lh]" />}
			<div
				part="inner-shadow"
				role="presentation"
				className={clsx(
					"pointer-events-none absolute -inset-px select-none rounded-sm",
					isRound && "aspect-square !rounded-full !px-0",
					isIconOnly && "aspect-square !px-0",
					variantInnerShadowClassMap[variant],
					innerShadowClassName,
				)}
			/>
		</TagAsAny>
	);
}

const variantClassMap: Record<ButtonVariant, string> = {
	primary: clsx(
		"text-primary-button-text",
		"bg-primary-button-background",
		"border-primary-button-border",
		"hover:bg-primary-button-background-hover",
		"hover:border-primary-button-border-hover",
		"active:bg-primary-button-background-pressed",
		"active:border-primary-button-border-pressed",
		"disabled:text-primary-button-disabled-text",
		"disabled:bg-primary-button-disabled-background",
		"disabled:border-primary-button-disabled-border",
		"aria-disabled:text-primary-button-disabled-text",
		"aria-disabled:bg-primary-button-disabled-background",
		"aria-disabled:border-primary-button-disabled-border",
	),
	secondary: clsx(
		"text-secondary-button-text",
		"bg-secondary-button-background",
		"border-secondary-button-border",
		"hover:bg-secondary-button-background-hover",
		"hover:border-secondary-button-border-hover",
		"active:bg-secondary-button-background-pressed",
		"active:border-secondary-button-border-pressed",
		"disabled:text-secondary-button-disabled-text",
		"disabled:bg-secondary-button-disabled-background",
		"disabled:border-secondary-button-disabled-border",
		"aria-disabled:text-secondary-button-disabled-text",
		"aria-disabled:bg-secondary-button-disabled-background",
		"aria-disabled:border-secondary-button-disabled-border",
	),
	text: clsx(
		"text-text-link",
		"hover:text-text-link-hover",
		"active:text-text-link-pressed",
		"border-transparent",
		"background-transparent",
		"p-0 min-h-[initial]",
		"disabled:text-text-disabled",
		"leading-[inherit]",
		"border-none",
	),
};

const variantInnerShadowClassMap: Record<ButtonVariant, string> = {
	primary: clsx("shadow-primary-button-inner"),
	secondary: clsx("shadow-secondary-button-inner"),
	text: clsx("shadow-none"),
};

const variantShadowClassMap: Record<ButtonVariant, string> = {
	primary: clsx("shadow-primary-button"),
	secondary: clsx("shadow-secondary-button"),
	text: clsx("shadow-none"),
};
